import { Classes, MenuItem } from '@blueprintjs/core'
import { ItemPredicate, ItemRenderer, MultiSelect } from '@blueprintjs/select'
import classNames from 'classnames'
import React, { Component, KeyboardEvent, SyntheticEvent } from 'react'
import { TranslationFunction } from 'react-i18next'

import { LoadingIndicator } from 'modules/forms'

import { i18nKeys } from '../../../app.i18n'
import { identity, isBlank, isPresent, Keycodes } from '../../../shared'

import './multiselect-wrapper.scss'

export interface MultiselectOption {
  id: number
  name: string
  label: string
}

type Props = {
  hasButton?: boolean
  items: MultiselectOption[]
  selectedItems: MultiselectOption[]
  isLoading: boolean
  disabled?: boolean
  openOnKeyDown?: boolean
  translate?: TranslationFunction
  itemPredicate?: ItemPredicate<MultiselectOption>
  onAdd(item: MultiselectOption): void
  onRemove(index: number): void
  onInputChange(query: string): void
}

const MultiSelectFromBP = MultiSelect.ofType<MultiselectOption>()

export class MultiselectWrapper extends Component<Props> {
  render() {
    const {
      hasButton = false,
      items,
      selectedItems = [],
      isLoading,
      translate = identity,
      disabled = false,
      openOnKeyDown = true,
    } = this.props

    const MultiselectWrapperClassNames = classNames('multiselect-popover', {
      'multiselect-popover--has-button': hasButton,
    })

    return (
      <MultiSelectFromBP
        itemPredicate={this.props.itemPredicate}
        itemRenderer={this.renderItem}
        items={items}
        onItemSelect={this.handleItemSelect}
        resetOnSelect
        openOnKeyDown={openOnKeyDown}
        onQueryChange={this.handleInputChange}
        noResults={
          !isLoading ? (
            <MenuItem disabled text={translate(i18nKeys.core.common.multiselect.noResults)} />
          ) : (
            <LoadingIndicator />
          )
        }
        popoverProps={{
          className: MultiselectWrapperClassNames,
          usePortal: true,
        }}
        tagRenderer={this.renderTags}
        tagInputProps={{
          onRemove: this.handleRemove,
          disabled,
          onKeyDown: this.stopEnterPropagation,
        }}
        selectedItems={isPresent(selectedItems) ? selectedItems : undefined}
      />
    )
  }

  private stopEnterPropagation = (event: KeyboardEvent<HTMLElement>) => {
    if (event.keyCode === Keycodes.Enter) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  private handleInputChange = (query: string) => {
    this.props.onInputChange(query)
  }

  private handleItemSelect = (item: MultiselectOption, event?: SyntheticEvent<HTMLElement>) => {
    event!.preventDefault()
    event!.stopPropagation()

    if (!this.isItemSelected(item)) {
      this.props.onAdd(item)

      return
    }

    this.props.onRemove(this.getItemIndex(item))
  }

  private isItemSelected = (item: MultiselectOption): boolean => {
    const { selectedItems = [] } = this.props
    if (isBlank(selectedItems)) return false

    return selectedItems.some((value, index, array) => value.id === item.id)
  }

  private handleRemove = (_tag: string, index: number) => {
    this.deselectTag(index)
  }

  private deselectTag(index: number) {
    this.props.onRemove(index)
  }

  private renderTags(item: MultiselectOption) {
    return item.name
  }

  private getItemIndex(item: MultiselectOption): number {
    return this.props.selectedItems.findIndex((selected: MultiselectOption) => item.id === selected.id)
  }

  private renderItem: ItemRenderer<MultiselectOption> = (item, { modifiers, handleClick }) => {
    if (!modifiers.matchesPredicate) {
      return null
    }
    const classes = classNames({
      [Classes.ACTIVE]: modifiers.active,
      [Classes.INTENT_PRIMARY]: modifiers.active,
    })

    return (
      <MenuItem
        className={classes}
        icon={this.isItemSelected(item) ? 'tick' : 'blank'}
        key={`m-${item.id}`}
        label={item.label}
        onClick={handleClick}
        text={`${item.name}`}
        shouldDismissPopover={false}
      />
    )
  }
}

export default MultiselectWrapper



// WEBPACK FOOTER //
// ./src/app/shared/components/multiselect-wrapper/multiselect-wrapper.tsx